import { useState, useEffect } from 'react';
import { RefreshCw, Loader2, Check, X } from 'lucide-react';
import { Button } from '@medikei/ui';
import { useToast } from '@/components/ui/use-toast';

interface MemedSyncButtonProps {
  onSync: () => void;
  isLoading: boolean;
  lastSyncTime?: string;
  lastError?: string;
}

export function MemedSyncButton({
  onSync,
  isLoading,
  lastSyncTime,
  lastError,
}: MemedSyncButtonProps) {
  const [state, setState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const { toast } = useToast();

  useEffect(() => {
    if (isLoading) {
      setState('loading');
    }
  }, [isLoading]);

  useEffect(() => {
    if (state === 'success') {
      const timer = setTimeout(() => setState('idle'), 2000);
      return () => clearTimeout(timer);
    }
    if (state === 'error') {
      const timer = setTimeout(() => setState('idle'), 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [state]);

  const handleSync = () => {
    try {
      onSync();
      setState('idle');
    } catch {
      setState('error');
      toast({
        title: 'Erro na sincronização',
        description: lastError || 'Ocorreu um erro ao sincronizar com a Memed.',
        variant: 'error',
      });
    }
  };

  return (
    <div className="space-y-3">
      <Button
        onClick={handleSync}
        disabled={state === 'loading'}
        variant={state === 'success' ? 'secondary' : 'primary'}
        className="w-full"
      >
        {state === 'loading' ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Sincronizando...
          </>
        ) : state === 'success' ? (
          <>
            <Check className="h-4 w-4 mr-2" />
            Sincronizado!
          </>
        ) : state === 'error' ? (
          <>
            <X className="h-4 w-4 mr-2" />
            Erro
          </>
        ) : (
          <>
            <RefreshCw className="h-4 w-4 mr-2" />
            Sincronizar
          </>
        )}
      </Button>
      {lastSyncTime && state === 'idle' && (
        <p className="text-xs text-center text-muted-foreground">
          Última sinc: {new Date(lastSyncTime).toLocaleString('pt-BR')}
        </p>
      )}
    </div>
  );
}
